import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';

const VendorForm = ({ vendor, onSubmit, onCancel, isLoading }) => {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    mobile: '',
    email: '',
    relationship: 'VENDOR',
    gst: ''
  });
  const [errors, setErrors] = useState({});

  // Populate form data when vendor prop changes
  useEffect(() => {
    if (vendor) {
      setFormData({
        name: vendor.name || '',
        address: vendor.address || '',
        mobile: vendor.phone || '',
        email: vendor.email || '',
        relationship: vendor.relationship || 'VENDOR',
        gst: vendor.gst || ''
      });
    } else {
      // Reset form for new vendor
      setFormData({
        name: '',
        address: '',
        mobile: '',
        email: '',
        relationship: 'VENDOR',
        gst: ''
      });
    }
  }, [vendor]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    // Validate mobile format (basic validation) - only if provided and not "-"
    if (formData.mobile && formData.mobile !== '-' && formData.mobile.trim() !== '') {
      if (!/^[6-9]\d{9}$/.test(formData.mobile)) {
        newErrors.mobile = 'Invalid mobile number format';
      }
    }

    // Validate email format - only if provided and not "-"
    if (formData.email && formData.email !== '-' && formData.email.trim() !== '') {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Invalid email format';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Form.Group className="mb-3">
        <Form.Label>Name <span className="text-danger">*</span></Form.Label>
        <Form.Control
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          isInvalid={!!errors.name}
          placeholder="Enter vendor name"
        />
        <Form.Control.Feedback type="invalid">
          {errors.name}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Address <span className="text-danger">*</span></Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          name="address"
          value={formData.address}
          onChange={handleChange}
          isInvalid={!!errors.address}
          placeholder="Enter full address"
        />
        <Form.Control.Feedback type="invalid">
          {errors.address}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Mobile</Form.Label>
        <Form.Control
          type="tel"
          name="mobile"
          value={formData.mobile}
          onChange={handleChange}
          isInvalid={!!errors.mobile}
          placeholder="Enter mobile number or '-' if not available"
        />
        <Form.Control.Feedback type="invalid">
          {errors.mobile}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Email</Form.Label>
        <Form.Control
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          isInvalid={!!errors.email}
          placeholder="Enter email address or '-' if not available"
        />
        <Form.Control.Feedback type="invalid">
          {errors.email}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>GST Number</Form.Label>
        <Form.Control
          type="text"
          name="gst"
          value={formData.gst}
          onChange={handleChange}
          placeholder="Enter GST number (optional)"
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Relation to Company</Form.Label>
        <Form.Select
          name="relationship"
          value={formData.relationship}
          onChange={handleChange}
        >
          <option value="VENDOR">VENDOR</option>
          <option value="SUPPLIER">SUPPLIER</option>
          <option value="CUSTOMER">CUSTOMER</option>
          <option value="PARTNER">PARTNER</option>
          <option value="OTHER">OTHER</option>
        </Form.Select>
      </Form.Group>

      <div className="d-flex gap-2 justify-content-end">
        <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant="primary" type="submit" disabled={isLoading}>
          {isLoading && <Spinner animation="border" size="sm" className="me-2" />}
          {vendor ? 'Update Vendor' : 'Add Vendor'}
        </Button>
      </div>
    </Form>
  );
};

export default VendorForm;
